import { CakeOutlined, Female, LocationOnOutlined } from "@mui/icons-material";
import ShareIcon from "@mui/icons-material/Share";
import {
    Avatar,
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from 'dayjs';
import { useLogin } from "../../../context/Login/LoginContext";
import { User } from "../../../types/User";
import { getImagePath } from "../../../util/envFn";
import { getNullableString, getSpacedFromCamel_Cap } from "../../../util/string";
import { useSnackbar } from "notistack";
import UserImage from "../../../components/UserImage";

interface ProfileHeaderProps {
    userData: User;
    setUserData: React.Dispatch<React.SetStateAction<User | null>>;
    editMode: boolean;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
}

const ProfileHeader = ({
    userData,
    setUserData,
    editMode,
    onEdit,
    onSave,
    onCancel
}: ProfileHeaderProps) => {
    const { user } = useLogin();
    const isSelf = user?._id === userData._id;
    const isAdmin = user?.role === "admin";
    const { enqueueSnackbar } = useSnackbar();

    const handleChange = (field: string, value: any) => {
        setUserData((prevData) => {
            if (!prevData) return null;
            return {
                ...prevData,
                [field]: value
            };
        });
    };

    const handleShare = () => {
        const params = new URLSearchParams();
        params.set("id", userData._id);
        const link = `${window.location.origin}/#/profile?${params.toString()}`;

        navigator.clipboard.writeText(link)
            .then(() => {
                enqueueSnackbar("Profile link copied to clipboard", { variant: "success" });
            })
            .catch(err => {
                enqueueSnackbar("Failed to copy profile link, please try again or copy manually", { variant: "error" });
            });
    };

    return (
        <Paper sx={{ padding: 3, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item>
                    <UserImage image={userData.image} size={80} name={userData.fullName || userData.email} />
                </Grid>
                <Grid item xs>
                    {editMode ? (
                        <Box mb={1}>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <TextField
                                    size="small"
                                    label="Full Name"
                                    value={userData.fullName || ""}
                                    onChange={(e) => handleChange("fullName", e.target.value)}
                                />
                            </Stack>
                        </Box>
                    ) : (
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Typography variant="h5" gutterBottom>
                                {getNullableString(userData.fullName || userData.email, "No name provided yet.")}
                            </Typography>
                        </Stack>
                    )}

                    {editMode ? (
                        <Stack direction="row" alignItems="center" gap={2} mb={1}>
                            <FormControl size="small">
                                <InputLabel id="gender-select-label">Gender</InputLabel>
                                <Select
                                    labelId="gender-select-label"
                                    id="gender-select"
                                    value={userData.gender || ""}
                                    label="Gender"
                                    onChange={(e) => handleChange("gender", e.target.value)}
                                >
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="non-binary">Non-binary</MenuItem>
                                    <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                            <DatePicker
                                label="Birth Date"
                                value={userData.birthDate ? dayjs(userData.birthDate) : null}
                                onChange={(newDate) =>
                                    handleChange("birthDate", newDate?.toDate() || undefined)
                                }
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" gap={2} mb={1}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Female fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                                <Typography variant="body2" color="text.secondary">
                                    {getSpacedFromCamel_Cap(getNullableString(userData.gender, "Gender not set"))}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <CakeOutlined fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                                <Typography variant="body2" color="text.secondary">
                                    {userData.birthDate ? dayjs().diff(dayjs(userData.birthDate), 'years') + " years old" : "Birth date not set"}
                                </Typography>
                            </Box>
                        </Stack>
                    )}

                    {editMode ? (
                        <Box>
                            <TextField
                                size="small"
                                label="Location"
                                value={userData.location || ""}
                                onChange={(e) => handleChange("location", e.target.value)}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <LocationOnOutlined fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                            <Typography variant="body2" color="text.secondary">
                                {getNullableString(userData.location, "Location not set")}
                            </Typography>
                        </Box>
                    )}
                </Grid>
                {
                    (isSelf || isAdmin) && (
                        <Grid item>
                            {editMode ? (
                                <Stack direction="row" gap={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={onSave}
                                    >
                                        Save Profile
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={onCancel}
                                    >
                                        Cancel
                                    </Button>
                                </Stack>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onEdit}
                                >
                                    {isAdmin ? "Edit User Profile" : "Edit Profile"}
                                </Button>
                            )}
                        </Grid>
                    )
                }
                <Grid item>
                    <Tooltip title="Copy profile link to clipboard">
                        <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={handleShare}
                            color="secondary"
                        >
                            Share
                        </Button>
                    </Tooltip>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ProfileHeader; 